import { useEffect, useState } from "react";
import type { BlogHeading } from "@/blog/types";

/** Highlights the section being read; the list links to each H2. */
function useActiveHeading(ids: string[]) {
  const [active, setActive] = useState<string | null>(null);
  useEffect(() => {
    if (!ids.length || !("IntersectionObserver" in window)) return;
    const visible = new Map<string, boolean>();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => visible.set(entry.target.id, entry.isIntersecting));
        const current = ids.find((id) => visible.get(id));
        if (current) setActive(current);
      },
      { rootMargin: "-15% 0px -70% 0px" },
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [ids]);
  return active;
}

export function ArticleToc({ headings, label }: { headings: BlogHeading[]; label: string }) {
  const sections = headings.filter((heading) => heading.level === 2);
  const [ids] = useState(() => sections.map((heading) => heading.id));
  const active = useActiveHeading(ids);
  if (!sections.length) return null;
  return (
    <nav aria-label={label}>
      <ol className="space-y-0.5 text-sm">
        {sections.map((heading) => {
          const on = heading.id === active;
          return (
            <li key={heading.id}>
              <a
                href={`#${heading.id}`}
                aria-current={on ? "location" : undefined}
                className={`block border-l-2 py-1.5 pl-3.5 leading-snug transition-[color,border-color] duration-300 ${
                  on
                    ? "border-brand-secondary font-semibold text-brand-primary"
                    : "border-brand-primary/10 text-ink-soft hover:border-brand-secondary/60 hover:text-brand-primary"
                }`}
              >
                {heading.text}
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
